import katex from 'katex';

const SUBSCRIPT_DIGITS = '₀₁₂₃₄₅₆₇₈₉';

function digitToSubscript(ch: string): string {
  const i = ch.charCodeAt(0) - 48;
  if (i >= 0 && i <= 9) return SUBSCRIPT_DIGITS[i] ?? ch;
  return ch;
}

function escapeHtml(value: unknown): string {
  const s = value == null ? '' : String(value);
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** LaTeX spacer (`\ ` / `\,`) → spasi biasa. */
export function normalizeLatexChemSpacers(raw: string): string {
  return raw.replace(/\\[ ,]/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * `CN^-` / `Fe^3+` → `CN^{-}` / `Fe^{3+}` agar sama dengan notasi PDF (`^{…}`).
 * Bentuk yang sudah `^{…}` dibiarkan.
 */
export function normalizeUnbracedChemCarets(raw: string): string {
  return raw.replace(/\^(?!\{)(\d*[+\-]|[+\-]|\d+)/g, '^{$1}');
}

/**
 * ASCII ringan → huruf kimia:
 * `BOD_5` → BOD₅, `NH_3` → NH₃, `KMnO_4` → KMnO₄, `H2SO4` → H₂SO₄, `Total\ Coliform` → Total Coliform.
 */
export function formatPlainChemSubscripts(raw: string): string {
  const s = normalizeLatexChemSpacers(raw);
  if (!s) return '';
  return s
    .replace(/_\{(\d+)\}/g, (_m, digits: string) => [...digits].map(digitToSubscript).join(''))
    .replace(/_(\d+)/g, (_m, digits: string) => [...digits].map(digitToSubscript).join(''))
    .replace(/([A-Za-z\)])(\d+)/g, (_m, prefix: string, digits: string) => {
      return prefix + [...digits].map(digitToSubscript).join('');
    });
}

/**
 * Strings from legacy chemistry exports often use lightweight LaTeX:
 * superscripts like `^{**}`, complex subscripts `_{text}`, or `\ce{...}`.
 * `\ ` spacer dan `BOD_5` ditangani sebagai teks biasa, bukan KaTeX.
 */
export function shouldRenderAsChemLatex(raw: string): boolean {
  const s = normalizeUnbracedChemCarets(normalizeLatexChemSpacers(raw));
  if (s.length === 0 || s.length > 16_000) return false;
  if (/\^(?:\{)/.test(s)) return true;
  if (/\bce\{/i.test(s)) return true;
  if (/_\{(?!\d+\})/.test(s)) return true;
  return false;
}

/**
 * Input KaTeX untuk string yang sudah lolos `shouldRenderAsChemLatex` (notasi LaTeX kimia eksplisit).
 */
export function chemDisplayTextToKatexSource(raw: string | null | undefined): string {
  const s = raw == null ? '' : String(raw).trim();
  return normalizeUnbracedChemCarets(normalizeLatexChemSpacers(s));
}

export function renderChemLatexToHtml(latex: string): string {
  return katex.renderToString(latex, {
    output: 'html',
    displayMode: false,
    throwOnError: false,
    strict: 'ignore',
  });
}

/** `_5` / `_{5}` / digit setelah huruf → `<sub>` (sama seperti PDF quotation / berita acara). */
function applyHtmlChemSubscripts(escaped: string): string {
  return escaped
    .replace(/_\{(\d+)\}/g, '<sub>$1</sub>')
    .replace(/_(\d+)/g, '<sub>$1</sub>')
    .replace(/([A-Za-z\)])(\d+)/g, '$1<sub>$2</sub>');
}

function extractBraced(s: string, openIndex: number): { inner: string; end: number } | null {
  if (s[openIndex] !== '{') return null;
  let depth = 0;
  for (let i = openIndex; i < s.length; i += 1) {
    const ch = s[i];
    if (ch === '{') depth += 1;
    else if (ch === '}') {
      depth -= 1;
      if (depth === 0) return { inner: s.slice(openIndex + 1, i), end: i + 1 };
    }
  }
  return null;
}

function hasInlineLatexScripts(s: string): boolean {
  return /\^\{/.test(s) || /_\{/.test(s) || /\\ce\{/i.test(s);
}

/**
 * Prosa + notasi LaTeX (`Cyanide, CN^{-}` / `Hexavalent Chromium; Cr^{6+}`)
 * tidak boleh dikirim utuh ke KaTeX. `^{…}` / `_{…}` jadi HTML `<sup>` / `<sub>`.
 */
function renderInlineChemLatexToHtml(raw: string): string {
  let i = 0;
  let out = '';

  const appendText = (text: string) => {
    if (!text) return;
    out += applyHtmlChemSubscripts(escapeHtml(text));
  };

  while (i < raw.length) {
    const ceMatch = raw.slice(i).match(/^\\ce\{/i);
    if (ceMatch) {
      const braced = extractBraced(raw, i + ceMatch[0].length - 1);
      if (braced) {
        out += renderInlineChemLatexToHtml(braced.inner);
        i = braced.end;
        continue;
      }
    }

    if ((raw.startsWith('^{', i) || raw.startsWith('_{', i)) && raw[i + 1] === '{') {
      const isSup = raw[i] === '^';
      const braced = extractBraced(raw, i + 1);
      if (braced) {
        const tag = isSup ? 'sup' : 'sub';
        out += `<${tag}>${renderInlineChemLatexToHtml(braced.inner)}</${tag}>`;
        i = braced.end;
        continue;
      }
    }

    let next = raw.length;
    const hat = raw.indexOf('^{', i);
    const sub = raw.indexOf('_{', i);
    const ceIdx = raw.slice(i).search(/\\ce\{/i);
    if (hat >= 0) next = Math.min(next, hat);
    if (sub >= 0) next = Math.min(next, sub);
    if (ceIdx >= 0) next = Math.min(next, i + ceIdx);
    if (next === i) {
      appendText(raw[i] ?? '');
      i += 1;
      continue;
    }
    appendText(raw.slice(i, next));
    i = next;
  }

  return out;
}

/**
 * Satu segmen teks parameter → HTML aman untuk UI (sama pipeline PDF quotation / berita acara).
 * Contoh: `Cyanide, CN^-` → `Cyanide, CN<sup>-</sup>`, `BOD_5` → `BOD<sub>5</sub>`.
 */
export function renderChemTextToHtml(raw: string | null | undefined): string {
  const s = raw == null ? '' : String(raw).trim();
  if (!s) return '';
  const spaced = normalizeUnbracedChemCarets(normalizeLatexChemSpacers(s));
  if (hasInlineLatexScripts(spaced)) {
    return renderInlineChemLatexToHtml(spaced);
  }
  if (shouldRenderAsChemLatex(spaced)) {
    return renderChemLatexToHtml(chemDisplayTextToKatexSource(spaced));
  }
  return applyHtmlChemSubscripts(escapeHtml(spaced));
}

function stripHtmlToPlain(html: string): string {
  if (!html) return '';
  if (typeof document !== 'undefined') {
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent ?? div.innerText ?? '').trim();
  }
  return html
    .replace(/<sup>(.*?)<\/sup>/gi, '^$1')
    .replace(/<sub>(.*?)<\/sub>/gi, '_$1')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
}

/** Ringkas KaTeX ke teks (bila `document` ada); untuk konteks string-only / sort / export. */
export function katexChemSourceToApproxPlainString(latexSource: string): string {
  const s = latexSource.trim();
  if (!s) return '';
  return stripHtmlToPlain(renderChemTextToHtml(s)) || s;
}

/**
 * Ringkasan string: sama seperti tampilan PDF/UI.
 */
export function formatChemicalNotation(raw: string | null | undefined): string {
  const s = raw == null ? '' : String(raw).trim();
  if (!s) return '';
  return stripHtmlToPlain(renderChemTextToHtml(s));
}

function nameAlreadyIncludesSymbol(nameRaw: string, symRaw: string): boolean {
  if (!nameRaw || !symRaw || symRaw === '-') return false;
  if (nameRaw === symRaw) return true;
  const parts = nameRaw.split(/[;,]/).map((s) => s.trim()).filter(Boolean);
  return parts.some((p) => p === symRaw) || nameRaw.includes(symRaw);
}

/** Gabungan nama + simbol untuk ringkasan / sel tabel berbasis string (hindari duplikasi rumus). */
export function formatParameterNameWithSymbol(
  nama: string | null | undefined,
  simbol: string | null | undefined,
): string {
  const nameRaw = nama == null ? '' : String(nama).trim();
  const symRaw = simbol == null ? '' : String(simbol).trim();
  const n = formatChemicalNotation(nama);
  const sym = formatChemicalNotation(simbol);
  if (n && sym && symRaw && symRaw !== '-') {
    if (nameAlreadyIncludesSymbol(nameRaw, symRaw)) return n;
    return `${n} (${sym})`;
  }
  return n || sym || '';
}

/** HTML gabungan nama + simbol — sama aturan PDF `renderParameterLabelHtml`. */
export function renderParameterLabelHtml(
  nama: string | null | undefined,
  simbol: string | null | undefined,
): string {
  const nameRaw = nama == null ? '' : String(nama).trim();
  const symRaw = simbol == null ? '' : String(simbol).trim();
  const n = renderChemTextToHtml(nama);
  const sym = renderChemTextToHtml(simbol);
  if (n && sym && symRaw && symRaw !== '-') {
    if (nameAlreadyIncludesSymbol(nameRaw, symRaw)) return n;
    return `${n} (${sym})`;
  }
  return n || sym || '';
}
